import { useEffect, useMemo, useRef, useState } from 'react'

import { useQueries } from '@tanstack/react-query'
import { CellClickedEvent, CellValueChangedEvent } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { AlertTriangle, Delete, FilePlus, Play, Plus, Send } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { AgGridWidget } from '@fortem-monorepo/react-ag'
import { CustomMenuItem } from '@fortem-monorepo/react-ag'
import { createContextMenu } from '@fortem-monorepo/react-ag'
import { useResizableState } from '@fortem-monorepo/react-hooks'
import { HeaderConfigBar, useApi } from '@fortem-monorepo/react-spa'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { Alert, AlertDescription, AlertTitle } from '@fortem-monorepo/react-ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@fortem-monorepo/react-ui'
import { Posmon } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { FLOW } from '@/constants/links'
import {
  createHistoricalPricesColDefs,
  createflowMonitorColDefs,
  scheduleColDefs,
  tradeblotterColDefs,
} from '@/pages/flow/posmon/col-defs'
import { PanelToggle } from '@/pages/operations/mappings/page'
import {
  useCreateFinalFactsheetMutation,
  useCreateIndicativeFactsheetMutation,
} from '@/react-query/mutations/factsheets'
import {
  useSendSecondaryEmailMutation,
  useUpdateHistoricalPricesMutation,
  useUpdateStructuredProductMutation,
} from '@/react-query/mutations/posmon'
import { useRunStressTestsMutation } from '@/react-query/mutations/pricing-interface'
import {
  useAddScheduleToWebsiteMutation,
  useAddSecurityToWebsiteMutation,
  useRemoveSecurityFromWebsiteMutation,
} from '@/react-query/mutations/website'

export interface FlowPosmonPageProps {}

export const FlowPosmonPage: React.FC<FlowPosmonPageProps> = ({}) => {
  const [_summary, _details] = useResizableState(2)
  const [instrument, setInstrument] = useState<Posmon.GetSummaryData[number]>()

  const params = {
    isin: instrument?.isin!,
    instrument_id: instrument?.instrument_id!,
  }

  const api = useApi()

  const { hasPermissions } = useUserContext()

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_FLOW_POSMON],
  }))

  const [guarantors, issuers] = useQueries({
    queries: [
      {
        queryKey: ['misc', 'guarantors'],
        queryFn: () => api.misc.getGuarantors(),
      },
      {
        queryKey: ['misc', 'issuers'],
        queryFn: () => api.misc.getIssuers(),
      },
    ],
  })

  const [guarantorData, issuerData] = useMemo(() => {
    return [
      (guarantors.data ?? []).map((item) => item.guarantor_name),
      (issuers.data ?? []).map((item) => item.issuer_name),
    ]
  }, [guarantors.data, issuers.data])

  const queries = useQueries({
    queries: [
      {
        queryKey: ['flow', 'posmon', 'summary', 'both'],
        queryFn: () => api.posmon.getSummary({ status: 'both' }),
      },
      {
        queryKey: ['flow', 'posmon', 'tradeblotter', params],
        queryFn: () => api.posmon.getSmartradeTrades(params),
        enabled: !!instrument,
      },
      {
        queryKey: ['flow', 'posmon', 'schedule', params],
        queryFn: () => api.posmon.getSchedule(params),
        enabled: !!instrument,
      },
      {
        queryKey: ['flow', 'posmon', 'secondary-historical', params],
        queryFn: () => api.posmon.getSecondaryHistorical(params),
        enabled: !!instrument,
      },
    ],
  })

  const [summary, trades, schedule, historical] = queries

  const [summaryData, tradesData, scheduleData, historicalData] = useMemo(() => {
    return [summary.data ?? [], trades?.data ?? [], schedule?.data ?? [], historical?.data ?? []]
  }, [summary.data, trades?.data, schedule?.data, historical?.data])

  const [dialogInfo, setDialogInfo] = useState<{
    isin?: string
    structure_name?: string
    value_date?: string
    to_dev?: boolean
  }>({})

  const updateStructuredProductMutation = useUpdateStructuredProductMutation()
  const updateHistoricalProcuesMutation = useUpdateHistoricalPricesMutation()

  const addSecurityToWebsiteMutation = useAddSecurityToWebsiteMutation()
  const removeSecurityFromWebsiteMutation = useRemoveSecurityFromWebsiteMutation()
  const addScheduleToWebsiteMutation = useAddScheduleToWebsiteMutation()
  const runStressTestsMutation = useRunStressTestsMutation()
  const createIndicativeFactsheetMutation = useCreateIndicativeFactsheetMutation()
  const createFinalFactsheetMutation = useCreateFinalFactsheetMutation()

  const { Dialog: AddSecurityDialog, withConfirmation: confirmAddSecurityToWebsite } =
    useConfirmModal({
      title: `Add Security to Website - ${dialogInfo.to_dev ? 'DEV' : 'PROD'}`,
      description: (
        <div className={'space-y-2'}>
          <p>Confirm you want to add {dialogInfo.structure_name} to website entries?</p>
          <p>If the entry already exists its data will be overriden.</p>
          <Alert variant={'destructive'}>
            <AlertTriangle />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription></AlertDescription>
          </Alert>
        </div>
      ),
    })

  const { Dialog: RemoveSecurityDialog, withConfirmation: confirmRemoveSecurityFromWebsite } =
    useConfirmModal({
      title: `Remove Security from Website - ${dialogInfo.to_dev ? 'DEV' : 'PROD'}`,
      description: (
        <div className={'space-y-2'}>
          <p>Confirm you want to remove {dialogInfo.structure_name} from website entries?</p>
          <p>It will be removed until added in the future.</p>
          <Alert variant={'destructive'}>
            <AlertTriangle />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription></AlertDescription>
          </Alert>
        </div>
      ),
    })

  const { Dialog: AddScheduleDialog, withConfirmation: confirmAddScheduleToWebsite } =
    useConfirmModal({
      title: `Add Schedule to Website - ${dialogInfo.to_dev ? 'DEV' : 'PROD'}`,
      description: `Confirm you want to add ${dialogInfo.structure_name} to website schedules? \r\nIf the entry already exists its data will be overriden.`,
    })

  const { Dialog: RunStressTestsDialog, withConfirmation: confirmRunStressTests } = useConfirmModal(
    {
      title: `Run Stresstests - for Strike Date`,
      description: `Run stresstests for ${dialogInfo.structure_name} (takes ~2min)?\r\n\r\nWill run with:\r\n-Value Date: ${dialogInfo.value_date}\r\n-Instance: LOCALINST1`,
    },
  )

  const gridRef = useRef<AgGridReact>(null)

  const [expired, setExpired] = useState<'alive' | 'expired' | 'both'>('both')

  useEffect(() => {
    if (gridRef.current?.api) {
      if (expired == 'alive' || expired === 'expired') {
        const filterMap = {
          alive: 'false',
          expired: 'true',
        } as const

        const filter = filterMap[expired]
        gridRef.current?.api.setFilterModel({
          expired: {
            filterType: 'set',
            values: [filter],
          },
        })
      } else {
        gridRef.current?.api.setFilterModel({
          expired: null,
        })
      }
    }
  }, [expired])

  // TODO: Will Sambells - ask Elie about these two functions
  const getSummaryContextMenuItems = createContextMenu<Posmon.GetSummaryData[number]>((_params) => {
    const isin = _params.node?.data?.isin!
    const unique_identifier = _params.node?.data?.unique_identifier!
    const strike_date = _params.node?.data?.strike_date!

    setDialogInfo({
      isin,
      structure_name: unique_identifier,
      value_date: strike_date,
      to_dev: false,
    })

    type PosmonKey = keyof Posmon.GetSummaryData[number]

    const marketingFields: PosmonKey[] = ['priority_ranking', 'region', 'asset', 'access']

    const canAddToWebsite = Object.entries(_params.node?.data ?? {})
      .filter(([key]) => marketingFields.includes(key as PosmonKey))
      .every(([_, val]) => val !== undefined && val !== null && !!val)

    const isAlreadyOnWebsite = !!_params.node?.data?.include_on_website

    return [
      ...(hasWriteAccess && !_params.node?.group
        ? ([
            ...(isAlreadyOnWebsite
              ? [
                  {
                    name: 'Remove Security from Website',
                    icon: renderToString(<Delete size={16} />),
                    action: () => {
                      return confirmRemoveSecurityFromWebsite(() =>
                        removeSecurityFromWebsiteMutation.mutateAsync({ isin }),
                      )
                    },
                  },
                ]
              : [
                  {
                    name: 'Add Security to Website',
                    icon: renderToString(<Plus size={16} />),
                    disabled: !canAddToWebsite,
                    action: () => {
                      return confirmAddSecurityToWebsite(() =>
                        addSecurityToWebsiteMutation.mutateAsync({ isin }),
                      )
                    },
                  },
                ]),

            {
              name: 'Add Schedule to Website',
              icon: renderToString(<Plus size={16} />),
              action: () => {
                return confirmAddScheduleToWebsite(() =>
                  addScheduleToWebsiteMutation.mutateAsync({ isin }),
                )
              },
            },
            'separator',
            {
              name: 'Run Stresstests - for Strike Date',
              icon: renderToString(<Play size={16} />),
              action: () => {
                return confirmRunStressTests(() =>
                  runStressTestsMutation.mutateAsync({
                    instance_tag: 'LOCALINST1',
                    structure_name: unique_identifier,
                    value_date: strike_date,
                  }),
                )
              },
            },
            'separator',
            {
              name: 'Create Factsheet - Indicative',
              icon: renderToString(<FilePlus size={16} />),
              action: () => {
                return createIndicativeFactsheetMutation.mutateAsync({
                  isin,
                  structure_name: unique_identifier,
                  strike_date,
                })
              },
            },
            {
              name: 'Create Factsheet - Final',
              icon: renderToString(<FilePlus size={16} />),
              action: () => {
                return createFinalFactsheetMutation.mutateAsync({
                  isin,
                  structure_name: unique_identifier,
                  strike_date,
                })
              },
            },
          ] satisfies CustomMenuItem[])
        : []),
      'separator',
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'export',
    ]
  })

  const flowMonitorColDefs = useMemo(() => {
    return createflowMonitorColDefs(hasWriteAccess, guarantorData, issuerData)
  }, [hasWriteAccess, guarantorData, issuerData])

  const sendSecondaryEmailMutation = useSendSecondaryEmailMutation()

  return (
    <Page metadata={{ title: 'Flow Posmon' }} permissions={FLOW.POSMON.permissions}>
      <AddSecurityDialog />
      <RemoveSecurityDialog />
      <AddScheduleDialog />
      <RunStressTestsDialog />
      <HeaderConfigBar className={'p-0!'}>
        <PanelToggle
          open={_summary.open}
          onClick={() => _summary.toggle()}
          className={'border-r rounded-none'}
        >
          Summary
        </PanelToggle>
        <PanelToggle
          open={_details.open}
          onClick={() => _details.toggle()}
          className={'border-r rounded-none'}
        >
          Details
        </PanelToggle>
      </HeaderConfigBar>
      <ViewContainer className={'h-full'}>
        <div className="flex items-end gap-4 h-full">
          {_summary.open && (
            <div className="flex-3 flex flex-col gap-2 h-full">
              <div className="flex items-center gap-1">
                <Tabs value={expired} onValueChange={(val) => setExpired(val as typeof expired)}>
                  <TabsList>
                    <TabsTrigger value={'both'}>Both</TabsTrigger>
                    <TabsTrigger value={'alive'}>Alive</TabsTrigger>
                    <TabsTrigger value={'expired'}>Expired</TabsTrigger>
                  </TabsList>
                </Tabs>
                <LoadingButton
                  size="sm"
                  className={'ml-4'}
                  variant={'outline'}
                  icon={<Send />}
                  onClick={() => sendSecondaryEmailMutation.mutateAsync()}
                >
                  Send secondary email
                </LoadingButton>
              </div>
              <div className="h-10 flex items-end">
                <span className={'font-medium text-gray-700'}>
                  {summary.isLoading && 'Loading instruments...'}
                  {summary.data?.length && 'Click on a row to select an ISIN'}
                </span>
              </div>
              <AgGridWidget
                query={summary}
                widget={{ className: 'h-full' }}
                widgetHeader={{
                  title: `Flow Monitor - ${status === 'both' ? 'all' : status} products`,
                }}
                grid={{
                  ref: gridRef,
                  rowData: summaryData,
                  columnDefs: flowMonitorColDefs,
                  getContextMenuItems: getSummaryContextMenuItems,
                  onCellClicked: (params: CellClickedEvent<Posmon.GetSummaryData[number]>) => {
                    setInstrument(params.data)
                  },
                  onCellValueChanged: (
                    params: CellValueChangedEvent<Posmon.GetSummaryData[number]>,
                  ) => {
                    return updateStructuredProductMutation.mutateAsync({
                      rowData: params.data,
                      oldValue: params.oldValue,
                      newValue: params.newValue,
                      updatedField: params.colDef.field!,
                    })
                  },
                  domLayout: 'normal',
                }}
              />
            </div>
          )}
          {_details.open && (
            <div className={'flex-2 flex flex-col  h-full'}>
              <Tabs defaultValue={'trades'} className={'h-full'}>
                <div className="posmon-tabs-header flex items-center justify-between gap-2">
                  <TabsList>
                    <TabsTrigger value={'trades'}>Trades</TabsTrigger>
                    <TabsTrigger value={'schedule'}>Schedule</TabsTrigger>
                    <TabsTrigger value={'prices'}>Prices</TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value={'trades'}>
                  <AgGridWidget
                    query={trades}
                    widget={{ className: 'h-full' }}
                    widgetHeader={{
                      title: `Smartrade Trades${params.isin ? ` - ${params.isin}` : ''}`,
                    }}
                    grid={{
                      rowData: tradesData,
                      columnDefs: tradeblotterColDefs,
                      domLayout: 'normal',
                    }}
                  />
                </TabsContent>
                <TabsContent value={'schedule'}>
                  <AgGridWidget
                    query={schedule}
                    widget={{ className: 'h-full' }}
                    widgetHeader={{ title: `Schedule${params?.isin ? ` - ${params.isin}` : ''}` }}
                    grid={{
                      rowData: scheduleData,
                      columnDefs: scheduleColDefs,
                      domLayout: 'normal',
                    }}
                  />
                </TabsContent>
                <TabsContent value={'prices'}>
                  <AgGridWidget
                    query={historical}
                    widget={{ className: 'h-full' }}
                    widgetHeader={{
                      title: `Historical Prices${params?.isin ? ` - ${params.isin}` : ''}`,
                    }}
                    grid={{
                      rowData: historicalData,
                      columnDefs: createHistoricalPricesColDefs(),
                      onCellValueChanged: (
                        params: CellValueChangedEvent<Posmon.GetSecondaryHistoricalData[number]>,
                      ) => {
                        return updateHistoricalProcuesMutation.mutateAsync({
                          rowData: params.data,
                          oldValue: params.oldValue,
                          newValue: params.newValue,
                          updatedField: params.colDef.field!,
                        })
                      },
                      domLayout: 'normal',
                    }}
                  />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </ViewContainer>
    </Page>
  )
}
